import type { InvoicePart, InvoiceType } from '../types/invoice';
import { vendorNamesMatchForProbableMatch } from './invoiceVendorMatch';
import {
    resolveDefaultNexFolderNo,
    resolveNexFolderNoForApDistLine,
} from './nexFolderResolution';
import {
    isSalesTaxLineItemPart,
    NEX_SALES_TAX_DIST_DESCRIPTION,
    shouldSynthesizeSalesTaxApDistFromHeader,
} from './salesTaxAsLineItem';

export type NexPurchaseOrderRow = {
    id: number;
    orderNumber: string;
    vendorName: string;
    orderedDate: string;
    folder?: number;
    detailLines?: Array<{ lineNumber: number; partNumber: string; unitCost: number; poHeaderID?: number }>;
};

export function getInvoiceVendorForManualPo(supplierText?: string | null): string | null {
    const s = (supplierText ?? '').trim();
    return s || null;
}

export function getEligiblePurchaseOrdersForVendor(
    nexResponse: NexPurchaseOrderRow[] | null | undefined,
    invoiceVendorName: string | null | undefined
): NexPurchaseOrderRow[] {
    if (!nexResponse?.length) return [];
    const v = (invoiceVendorName ?? '').trim();
    if (!v) return [...nexResponse];
    return nexResponse.filter((po) => vendorNamesMatchForProbableMatch(v, po.vendorName));
}

export function tryAutoResolveOrphanManualPoHeaderId(args: {
    nexResponse: NexPurchaseOrderRow[];
    invoiceVendorName: string | null | undefined;
    matchedOtherLinePoHeaderIds: number[];
    totalApDistRowCount: number;
}): number | null {
    const { nexResponse, invoiceVendorName, matchedOtherLinePoHeaderIds, totalApDistRowCount } = args;
    if (totalApDistRowCount <= 1) return null;
    const uniq = new Set(matchedOtherLinePoHeaderIds);
    if (uniq.size !== 1) return null;
    const onlyId = Array.from(uniq)[0];
    const eligible = getEligiblePurchaseOrdersForVendor(nexResponse, invoiceVendorName);
    if (!eligible.some((po) => po.id === onlyId)) return null;
    return onlyId;
}

/** True if `poHeaderId` is a PO header on this folder's NEX response (manual orphan lines may attach to any folder PO). */
export function isPoHeaderIdEligibleForManualOrphan(
    poHeaderId: number,
    nexResponse: NexPurchaseOrderRow[] | null | undefined,
    _invoiceVendorName?: string | null | undefined
): boolean {
    if (!nexResponse?.length) return false;
    return nexResponse.some((po) => po.id === poHeaderId);
}

type RowOpts = { isFreight?: boolean; isFuel?: boolean; isRestocking?: boolean };

interface PreOrphanMapping {
    poHeaderID: number | null;
    poLineNo: number | null;
    hasNexMapping: boolean;
}

function normalizePartNumber(value: string | undefined): string {
    return (value ?? '').replace(/[\s\-_]/g, '').toUpperCase().trim();
}

function computePreOrphanMapping(
    idx: number,
    src: InvoicePart,
    ctx: {
        unmatchedPartIndexSet: Set<number>;
        manualMatchedPartIndexSet: Set<number>;
        flaggedAsMissingOrDifferentPartIndexSet: Set<number>;
        matchedNexLines: Array<{ partIndex: number; nexLine: { poHeaderID?: number; lineNumber?: number } }>;
        allNexDetailLines: Array<{
            poHeaderID: number;
            lineNumber: number;
            partNumber: string;
            unitCost: number;
            vendorName: string;
        }>;
        nexResponse: NexPurchaseOrderRow[] | null;
        invoiceVendorForPartMapping: string | null;
    }
): PreOrphanMapping {
    const isExplicitlyUnmatched = ctx.unmatchedPartIndexSet.has(idx);
    const match = ctx.matchedNexLines.find((m) => m.partIndex === idx);
    const isUserManualMatch = ctx.manualMatchedPartIndexSet.has(idx);
    const isFlaggedAsMissingOrDifferent = ctx.flaggedAsMissingOrDifferentPartIndexSet.has(idx);

    let poHeaderID: number | null = null;
    let poLineNo: number | null = null;
    let hasNexMapping = false;

    if (isExplicitlyUnmatched || (!isUserManualMatch && isFlaggedAsMissingOrDifferent)) {
        // orphan candidate
    } else if (match) {
        const defaultPoHeaderId = (ctx.nexResponse && ctx.nexResponse[0]?.id) ?? null;
        poHeaderID = match.nexLine.poHeaderID ?? defaultPoHeaderId ?? null;
        poLineNo = match.nexLine.lineNumber ?? null;
        hasNexMapping = poHeaderID != null && poLineNo != null;
    } else if (ctx.allNexDetailLines.length > 0 && src.partNumber) {
        const norm = normalizePartNumber(src.partNumber);
        const invV = ctx.invoiceVendorForPartMapping;
        const candidates = ctx.allNexDetailLines.filter((dl) => {
            if (!dl.partNumber) return false;
            if (normalizePartNumber(dl.partNumber) !== norm) return false;
            if (!invV) return true;
            return vendorNamesMatchForProbableMatch(invV, dl.vendorName);
        });

        if (candidates.length === 1) {
            poHeaderID = candidates[0].poHeaderID;
            poLineNo = candidates[0].lineNumber;
            hasNexMapping = true;
        } else if (candidates.length > 1 && typeof src.unitBuyPrice === 'number') {
            const target = Math.abs(src.unitBuyPrice);
            let best = candidates[0];
            let bestDiff = Math.abs(candidates[0].unitCost - target);
            for (let i = 1; i < candidates.length; i++) {
                const diff = Math.abs(candidates[i].unitCost - target);
                if (diff < bestDiff) {
                    best = candidates[i];
                    bestDiff = diff;
                }
            }
            poHeaderID = best.poHeaderID;
            poLineNo = best.lineNumber;
            hasNexMapping = true;
        }
    }

    return { poHeaderID, poLineNo, hasNexMapping };
}

export interface ManualPoLineInfo {
    globalIndex: number;
    description: string;
    autoPoHeaderId: number | null;
    eligible: Array<{ poHeaderID: number; orderNumber: string; vendorName: string; orderedDateLabel: string }>;
}

export interface NexSubmitApDistPreviewResult {
    apDist: Array<{
        /** Global line index (line items + freight + fuel + restocking) — matches manual PO selection keys. */
        globalIndex: number;
        folderNo: number;
        poHeaderID: number | null;
        poLineNo: number | null;
        description: string;
        unitList: number;
        unitCost: number;
        units: number;
        totalCost: number;
        /** `0` = matched, `1` = manual/unmatched, `2` = Tesla restocking or sales-tax line. */
        manualLineInd: 0 | 1 | 2;
    }>;
    submitBlocked: boolean;
    manualPoLineInfos: ManualPoLineInfo[];
    /** Billable rows (line/freight/fuel) submitting as manual NEX lines without a PO line match. */
    unmatchedManualLineCount: number;
}

/**
 * Mirrors server `buildNexInvoicePayload` mapping for submit preview + manual PO UI.
 * For Pay Type Charge, `restockingFeeItems` should exclude credit-card surcharge lines (same as `updateInvoice` → `filterOutCreditCardMiscFeeLinesForCharge`).
 */
export function computeNexSubmitApDistPreview(args: {
    roNumber: string;
    /** Matches server `buildNexInvoicePayload` credit-memo line qty / unit signs. */
    invoiceType: InvoiceType;
    lineItems: InvoicePart[];
    freightItems: InvoicePart[];
    fuelItems: InvoicePart[];
    restockingFeeItems: InvoicePart[];
    nexResponse: NexPurchaseOrderRow[] | null;
    matchedNexLines: Array<{ partIndex: number; nexLine: { poHeaderID?: number; lineNumber?: number } }>;
    unmatchedPartIndices: number[];
    manualMatchedPartIndices: number[];
    flaggedAsMissingOrDifferentPartIndices: number[];
    invoiceSupplierText?: string | null;
    manualPoHeaderOverrides: Record<number, number | undefined>;
    /**
     * When true, unmatched / orphan rows are previewed as poHeaderID 0 / poLineNo 0 / manualLineInd 1
     * and do not block submit or require PO pickers.
     */
    allowNullPoHeaderForUnmatchedBillable?: boolean;
    unmatchedBillablePartIndices?: number[];
    /** Org setting: US sales tax as AP dist line (ignored for Canada / split). */
    salesTaxAsLineItem?: boolean;
    taxesTotal?: number;
    nexTaxFormat?: 'single' | 'split';
}): NexSubmitApDistPreviewResult | null {
    // Required on `args` for caller parity with `buildNexInvoicePayload`; preview row math uses stored line fields only.
    void args.invoiceType;

    const {
        roNumber,
        lineItems,
        freightItems,
        fuelItems,
        restockingFeeItems,
        nexResponse,
        matchedNexLines,
        unmatchedPartIndices,
        manualMatchedPartIndices,
        flaggedAsMissingOrDifferentPartIndices,
        invoiceSupplierText,
        manualPoHeaderOverrides,
        allowNullPoHeaderForUnmatchedBillable = false,
        unmatchedBillablePartIndices = [],
        salesTaxAsLineItem = false,
        taxesTotal = 0,
        nexTaxFormat,
    } = args;

    const folderFallback = resolveDefaultNexFolderNo(nexResponse, roNumber);
    if (folderFallback == null) return null;

    const toTwoDecimals = (value: number): number => Math.round((value || 0) * 100) / 100;
    const toSixDecimals = (value: number): number => Math.round((value || 0) * 1_000_000) / 1_000_000;

    const unmatchedPartIndexSet = new Set(unmatchedPartIndices);
    const manualMatchedPartIndexSet = new Set(manualMatchedPartIndices);
    const flaggedAsMissingOrDifferentPartIndexSet = new Set(flaggedAsMissingOrDifferentPartIndices);

    const allNexDetailLines: Array<{
        poHeaderID: number;
        lineNumber: number;
        partNumber: string;
        unitCost: number;
        vendorName: string;
    }> = nexResponse
        ? nexResponse.flatMap((po) =>
              (po.detailLines ?? []).map((dl) => ({
                  poHeaderID: po.id,
                  lineNumber: dl.lineNumber,
                  partNumber: dl.partNumber,
                  unitCost: dl.unitCost,
                  vendorName: String(po.vendorName ?? ''),
              }))
          )
        : [];

    const invoiceVendorForPartMapping = getInvoiceVendorForManualPo(invoiceSupplierText);

    const ctx = {
        unmatchedPartIndexSet,
        manualMatchedPartIndexSet,
        flaggedAsMissingOrDifferentPartIndexSet,
        matchedNexLines,
        allNexDetailLines,
        nexResponse,
        invoiceVendorForPartMapping,
    };

    const rows: Array<{ idx: number; src: InvoicePart; opts?: RowOpts }> = [];
    lineItems.forEach((p, idx) => rows.push({ idx, src: p }));
    const freightOffset = lineItems.length;
    freightItems.forEach((p, idx) => rows.push({ idx: freightOffset + idx, src: p, opts: { isFreight: true } }));
    const fuelOffset = freightOffset + freightItems.length;
    fuelItems.forEach((p, idx) => rows.push({ idx: fuelOffset + idx, src: p, opts: { isFuel: true } }));
    const restockingOffset = fuelOffset + fuelItems.length;
    restockingFeeItems.forEach((p, idx) => rows.push({ idx: restockingOffset + idx, src: p, opts: { isRestocking: true } }));

    const preOrphanByIdx = new Map<number, PreOrphanMapping>();
    for (const row of rows) {
        preOrphanByIdx.set(row.idx, computePreOrphanMapping(row.idx, row.src, ctx));
    }

    const totalApDistRowCount = rows.length;
    const invoiceVendorForPo = getInvoiceVendorForManualPo(invoiceSupplierText);
    const unmatchedBillableIndexSet = new Set<number>(unmatchedBillablePartIndices);
    const nexList = nexResponse ?? [];

    const resolveFinalMapping = (
        idx: number,
        pre: PreOrphanMapping,
        _rowOpts?: RowOpts
    ): { poHeaderID: number | null; poLineNo: number | null; hasNexMapping: boolean } => {
        if (pre.poHeaderID != null && pre.poLineNo != null && pre.hasNexMapping) {
            return { poHeaderID: pre.poHeaderID, poLineNo: pre.poLineNo, hasNexMapping: true };
        }

        // Org allows unmatched submit: any non-matched orphan (billable or restocking/misc)
        // posts with poHeaderID 0 + line 0 + manualLineInd.
        if (allowNullPoHeaderForUnmatchedBillable) {
            return { poHeaderID: 0, poLineNo: 0, hasNexMapping: false };
        }

        const fromManual = manualPoHeaderOverrides[idx];

        const matchedOtherLinePoHeaderIds: number[] = [];
        for (const row of rows) {
            if (row.idx === idx) continue;
            const other = preOrphanByIdx.get(row.idx);
            if (other?.hasNexMapping && other.poHeaderID != null) {
                matchedOtherLinePoHeaderIds.push(other.poHeaderID);
            }
        }

        const autoPo = tryAutoResolveOrphanManualPoHeaderId({
            nexResponse: nexList,
            invoiceVendorName: invoiceVendorForPo,
            matchedOtherLinePoHeaderIds,
            totalApDistRowCount,
        });

        let poHeaderID: number | null = autoPo ?? (typeof fromManual === 'number' && fromManual > 0 ? fromManual : null) ?? null;
        const poLineNo: number | null = 0;
        const hasNexMapping = false;

        if (poHeaderID != null && !isPoHeaderIdEligibleForManualOrphan(poHeaderID, nexResponse, invoiceVendorForPo)) {
            poHeaderID = null;
        }

        if (poHeaderID != null) {
            return { poHeaderID, poLineNo, hasNexMapping };
        }

        return {
            poHeaderID: null,
            poLineNo: null,
            hasNexMapping: false,
        };
    };

    const apDist: NexSubmitApDistPreviewResult['apDist'] = [];
    const manualPoLineInfos: ManualPoLineInfo[] = [];

    for (const row of rows) {
        const pre = preOrphanByIdx.get(row.idx)!;
        const src = row.src;
        const opts = row.opts;
        const isTeslaImpliedRestocking =
            !!opts?.isRestocking && src.teslaCreditImpliedRestockingFee === true;
        const isSalesTaxLine =
            !opts?.isRestocking && !opts?.isFreight && !opts?.isFuel && isSalesTaxLineItemPart(src);

        let poHeaderID: number | null;
        let poLineNo: number | null;
        let hasNexMapping: boolean;
        let manualLineInd: 0 | 1 | 2;

        if (isTeslaImpliedRestocking) {
            const parentIdx =
                typeof src.teslaRestockingSourceLineItemIndex === 'number' &&
                Number.isFinite(src.teslaRestockingSourceLineItemIndex)
                    ? src.teslaRestockingSourceLineItemIndex
                    : null;
            const parentPre = parentIdx != null ? preOrphanByIdx.get(parentIdx) : undefined;
            const parentResolved =
                parentPre != null ? resolveFinalMapping(parentIdx!, parentPre) : null;
            if (
                parentResolved &&
                parentResolved.poHeaderID != null &&
                parentResolved.poLineNo != null
            ) {
                poHeaderID = parentResolved.poHeaderID;
                poLineNo = parentResolved.poLineNo;
                hasNexMapping = false;
            } else if (allowNullPoHeaderForUnmatchedBillable) {
                poHeaderID = 0;
                poLineNo = 0;
                hasNexMapping = false;
            } else {
                const fallback = resolveFinalMapping(row.idx, pre, row.opts);
                poHeaderID = fallback.poHeaderID;
                poLineNo = fallback.poLineNo;
                hasNexMapping = false;
            }
            manualLineInd = 2;
        } else if (isSalesTaxLine) {
            let clonePoHeaderID: number | null = null;
            let clonePoLineNo: number | null = null;
            for (const other of rows) {
                if (other.idx === row.idx) continue;
                const otherPre = preOrphanByIdx.get(other.idx)!;
                const otherResolved = resolveFinalMapping(other.idx, otherPre, other.opts);
                if (
                    otherResolved.hasNexMapping &&
                    otherResolved.poHeaderID != null &&
                    otherResolved.poLineNo != null
                ) {
                    clonePoHeaderID = otherResolved.poHeaderID;
                    clonePoLineNo = otherResolved.poLineNo;
                    break;
                }
            }
            if (clonePoHeaderID != null && clonePoLineNo != null) {
                poHeaderID = clonePoHeaderID;
                poLineNo = clonePoLineNo;
                hasNexMapping = false;
            } else if (allowNullPoHeaderForUnmatchedBillable) {
                poHeaderID = 0;
                poLineNo = 0;
                hasNexMapping = false;
            } else {
                const fallback = resolveFinalMapping(row.idx, pre, row.opts);
                poHeaderID = fallback.poHeaderID;
                poLineNo = fallback.poLineNo;
                hasNexMapping = false;
            }
            manualLineInd = 2;
        } else {
            const resolved = resolveFinalMapping(row.idx, pre, row.opts);
            poHeaderID = resolved.poHeaderID;
            poLineNo = resolved.poLineNo;
            hasNexMapping = resolved.hasNexMapping;
            manualLineInd = hasNexMapping ? 0 : 1;
        }

        const rawUnitList = Number(src.listPrice ?? 0);
        const rawUnitCost = Number(src.unitBuyPrice ?? 0);
        const unitList = toSixDecimals(Number.isFinite(rawUnitList) ? rawUnitList : 0);
        const unitCost = toSixDecimals(Number.isFinite(rawUnitCost) ? rawUnitCost : 0);

        const qNum = Number(src.qty);
        const units = isTeslaImpliedRestocking || isSalesTaxLine
            ? 1
            : Number.isFinite(qNum)
              ? Math.trunc(qNum)
              : 1;

        const totalField = (src as { totalBuyPrice?: number; buyPrice?: number }).totalBuyPrice ?? (src as { buyPrice?: number }).buyPrice;
        const totalFromLine =
            typeof totalField === 'number' && Number.isFinite(totalField)
                ? totalField
                : unitCost * units;
        const totalCost = toTwoDecimals(totalFromLine);

        const desc = isSalesTaxLine
            ? NEX_SALES_TAX_DIST_DESCRIPTION
            : src.title || (opts?.isFreight ? 'Freight' : opts?.isFuel ? 'Fuel' : opts?.isRestocking ? 'Restocking Fee' : 'Part');

        const folderNo =
            resolveNexFolderNoForApDistLine(poHeaderID, nexResponse, roNumber) ?? folderFallback;

        apDist.push({
            globalIndex: row.idx,
            folderNo,
            poHeaderID,
            poLineNo,
            description: desc,
            unitList: isSalesTaxLine ? 0 : unitList,
            unitCost: isSalesTaxLine ? totalCost : unitCost,
            units,
            totalCost,
            manualLineInd,
        });

        if (!hasNexMapping && manualLineInd !== 2) {
            // Allowed orphans (including restocking/misc) do not need a PO picker.
            if (allowNullPoHeaderForUnmatchedBillable) {
                continue;
            }

            const matchedOtherLinePoHeaderIds: number[] = [];
            for (const r of rows) {
                if (r.idx === row.idx) continue;
                const o = preOrphanByIdx.get(r.idx);
                if (o?.hasNexMapping && o.poHeaderID != null) matchedOtherLinePoHeaderIds.push(o.poHeaderID);
            }
            const autoPo = tryAutoResolveOrphanManualPoHeaderId({
                      nexResponse: nexList,
                      invoiceVendorName: invoiceVendorForPo,
                      matchedOtherLinePoHeaderIds,
                      totalApDistRowCount,
                  });
            const sortedFolderPos = [...nexList].sort((a, b) => {
                const ta = new Date(a.orderedDate).getTime();
                const tb = new Date(b.orderedDate).getTime();
                const na = Number.isFinite(ta) ? ta : 0;
                const nb = Number.isFinite(tb) ? tb : 0;
                return nb - na;
            });
            const eligible = sortedFolderPos.map((po) => ({
                poHeaderID: po.id,
                orderNumber: po.orderNumber,
                vendorName: po.vendorName,
                orderedDateLabel: formatNexOrderedDate(po.orderedDate),
            }));
            manualPoLineInfos.push({
                globalIndex: row.idx,
                description: desc,
                autoPoHeaderId: autoPo,
                eligible,
            });
        }
    }

    const emitSalesTaxAsLine = shouldSynthesizeSalesTaxApDistFromHeader({
        salesTaxAsLineItem,
        nexTaxFormat,
        taxesTotal,
        lineItems,
    });

    if (emitSalesTaxAsLine) {
        let clonePoHeaderID: number | null = null;
        let clonePoLineNo: number | null = null;
        for (const row of rows) {
            const pre = preOrphanByIdx.get(row.idx)!;
            const resolved = resolveFinalMapping(row.idx, pre, row.opts);
            if (resolved.hasNexMapping && resolved.poHeaderID != null && resolved.poLineNo != null) {
                clonePoHeaderID = resolved.poHeaderID;
                clonePoLineNo = resolved.poLineNo;
                break;
            }
        }
        if (clonePoHeaderID == null || clonePoLineNo == null) {
            const withPo = apDist.find(
                (d) => d.poHeaderID != null && d.poHeaderID > 0 && d.poLineNo != null,
            );
            if (withPo) {
                clonePoHeaderID = withPo.poHeaderID;
                clonePoLineNo = withPo.poLineNo;
            } else if (allowNullPoHeaderForUnmatchedBillable) {
                clonePoHeaderID = 0;
                clonePoLineNo = 0;
            }
        }

        if (clonePoHeaderID != null && clonePoLineNo != null) {
            const taxAmount = toTwoDecimals(taxesTotal);
            const salesTaxGlobalIndex = restockingOffset + restockingFeeItems.length;
            const folderNo =
                resolveNexFolderNoForApDistLine(clonePoHeaderID, nexResponse, roNumber) ?? folderFallback;
            apDist.push({
                globalIndex: salesTaxGlobalIndex,
                folderNo,
                poHeaderID: clonePoHeaderID,
                poLineNo: clonePoLineNo,
                description: NEX_SALES_TAX_DIST_DESCRIPTION,
                unitList: 0,
                unitCost: taxAmount,
                units: 1,
                totalCost: taxAmount,
                manualLineInd: 2,
            });
        }
    }

    const submitBlocked = apDist.some((d) => {
        if (
            allowNullPoHeaderForUnmatchedBillable &&
            d.manualLineInd !== 0 &&
            d.poHeaderID === 0 &&
            d.poLineNo === 0
        ) {
            return false;
        }
        if (d.poHeaderID != null && d.poHeaderID > 0 && d.poLineNo != null) return false;
        return true;
    });

    // When sales tax should emit but we could not resolve a PO clone, block submit.
    if (
        emitSalesTaxAsLine &&
        !apDist.some((d) => d.description === NEX_SALES_TAX_DIST_DESCRIPTION && d.manualLineInd === 2)
    ) {
        return {
            apDist,
            submitBlocked: true,
            manualPoLineInfos,
            unmatchedManualLineCount: apDist.filter(
                (d) => d.manualLineInd !== 0 && unmatchedBillableIndexSet.has(d.globalIndex),
            ).length,
        };
    }

    const unmatchedManualLineCount = apDist.filter(
        (d) => d.manualLineInd !== 0 && unmatchedBillableIndexSet.has(d.globalIndex),
    ).length;

    return { apDist, submitBlocked, manualPoLineInfos, unmatchedManualLineCount };
}

function formatNexOrderedDate(iso: string | undefined): string {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return iso;
        return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
        return iso;
    }
}
